/**
 * FP3-044B Issue 2 — Cleaner date entry for admin-owned dates.
 *
 * Combines a MM/DD/YYYY text input with a shadcn calendar popover.
 * The text input is the source of truth: typing updates the parent
 * via onChange; the calendar popover writes to the same setter.
 *
 * Stores ISO YYYY-MM-DD on the parent (matches existing backend
 * shape). Displays MM/DD/YYYY in the input. Year navigation comes
 * via the calendar's built-in month dropdown + year dropdown when we
 * pass `captionLayout="dropdown"`.
 */
import { useEffect, useMemo, useState } from "react";
import { Calendar as CalendarIcon } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Treat dates as plain ISO strings — never `new Date()` on a bare
// YYYY-MM-DD (which the JS Date parser interprets as UTC midnight
// and shifts a day in negative timezones). We construct local-noon
// Date objects when the calendar needs a Date, and parse back to
// ISO without going through a UTC roundtrip.
function isoToDisplay(iso) {
  if (!iso || typeof iso !== "string" || iso.length < 10) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return "";
  return `${m}/${d}/${y}`;
}

function displayToIso(text) {
  if (!text) return "";
  const m = text.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null; // null = invalid; "" = blank
  const mm = String(parseInt(m[1], 10)).padStart(2, "0");
  const dd = String(parseInt(m[2], 10)).padStart(2, "0");
  const yyyy = m[3];
  // Sanity check the constructed date round-trips.
  const dt = new Date(`${yyyy}-${mm}-${dd}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return null;
  if (dt.getFullYear() !== Number(yyyy) || dt.getMonth() + 1 !== Number(mm) || dt.getDate() !== Number(dd)) {
    return null;
  }
  return `${yyyy}-${mm}-${dd}`;
}

export default function DateField({
  value,           // ISO YYYY-MM-DD or "" or null
  onChange,        // (isoOrEmpty: string) => void
  testid,
  placeholder = "MM/DD/YYYY",
  minYear = 1990,
  maxYear = new Date().getFullYear() + 5,
  disabled = false,
}) {
  const [text, setText] = useState(isoToDisplay(value));
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  // Re-sync when the parent ISO changes externally (e.g. server load).
  useEffect(() => {
    setText(isoToDisplay(value));
    setError("");
  }, [value]);

  const calendarDate = useMemo(() => {
    if (!value || typeof value !== "string" || value.length < 10) return undefined;
    const [y, m, d] = value.slice(0, 10).split("-");
    if (!y || !m || !d) return undefined;
    return new Date(`${y}-${m}-${d}T12:00:00`);
  }, [value]);

  const commitFromText = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      onChange?.("");
      setError("");
      return;
    }
    const iso = displayToIso(trimmed);
    if (iso === null) {
      setError("Use MM/DD/YYYY");
      return;
    }
    setError("");
    onChange?.(iso);
  };

  return (
    <div className="relative">
      <div className="flex items-stretch gap-1">
        <input
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
          value={text}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            setError("");
          }}
          onBlur={() => commitFromText(text)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitFromText(text);
          }}
          data-testid={testid}
          className="h-9 flex-1 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              data-testid={testid ? `${testid}-cal-trigger` : undefined}
              className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-[#d4d2cc] bg-white hover:bg-[#F3F1EC]"
              aria-label="Open calendar picker"
            >
              <CalendarIcon size={14} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={4}
            className="w-auto p-0"
            data-testid={testid ? `${testid}-cal-popover` : undefined}
          >
            <Calendar
              mode="single"
              selected={calendarDate}
              captionLayout="dropdown"
              fromYear={minYear}
              toYear={maxYear}
              defaultMonth={calendarDate || new Date()}
              onSelect={(dt) => {
                if (!dt) return;
                const yyyy = dt.getFullYear();
                const mm = String(dt.getMonth() + 1).padStart(2, "0");
                const dd = String(dt.getDate()).padStart(2, "0");
                const iso = `${yyyy}-${mm}-${dd}`;
                onChange?.(iso);
                setText(`${mm}/${dd}/${yyyy}`);
                setError("");
                setOpen(false);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      {error && (
        <p
          data-testid={testid ? `${testid}-error` : undefined}
          className="text-[10.5px] text-[#A8453A] mt-0.5"
        >
          {error}
        </p>
      )}
    </div>
  );
}
